"use client";

import type { AccountCustomerOutputDto } from "@sticker-giant/sg-frontend-common";
import { useQuery } from "@tanstack/react-query";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const CUSTOMER_QUERY_KEY = ["customer"] as const;
export const CUSTOMER_STALE_TIME = 300_000;

export function useCustomer() {
  const accountClient = useServicesStore(state => state.accountClientFetch);
  const customerId = useAuthClaimsStore(state => state.claims?.sg_customer_id);

  return useQuery<AccountCustomerOutputDto>({
    queryKey: [...CUSTOMER_QUERY_KEY, customerId],
    queryFn: async () => {
      if (!accountClient) throw new Error("Account client is not available");
      if (!customerId) throw new Error("Customer ID is not available");
      return await accountClient.getCustomerById(customerId);
    },
    enabled: !!accountClient && !!customerId,
    staleTime: CUSTOMER_STALE_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useCustomerSkus() {
  const authResolved = useServicesStore(state => state.authResolved);
  const customerId = useAuthClaimsStore(state => state.claims?.sg_customer_id);
  const { data, isSuccess, isError } = useCustomer();

  // Guests never run the query, so it stays `pending` forever — gating on isPending would
  // block their pricing indefinitely
  const isResolved = authResolved && (!customerId || isSuccess || isError);
  // Sorted so a reordered response from the account service cannot churn a consumer's query key
  const customerSkus = data?.skus ? [...data.skus].sort() : [];

  return { customerSkus, isResolved };
}
